import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { isActive, queueJobsAtom, updateJobAtom, type QueueJob } from '@/store/optimizationQueue';
import { STALE_SUGGESTION_CODE } from '@/hooks/useOptimizationReports';

type JobOutcome = Pick<QueueJob, 'id' | 'status' | 'errorCode' | 'message' | 'recordFailed'>;

async function runJob(job: QueueJob): Promise<JobOutcome> {
  try {
    const response = await api.optimizations.decide(
      job.executionId,
      job.reportDate,
      job.suggestionKey,
      job.decision
    );
    const recordFailed = job.decision !== 'reject' && response.applied === null && !!response.suggestion.error;
    return {
      id: job.id,
      status: 'done',
      errorCode: null,
      message: recordFailed ? response.suggestion.error : null,
      recordFailed,
    };
  } catch (error) {
    return {
      id: job.id,
      status: 'error',
      errorCode: error instanceof ApiError ? error.code ?? null : null,
      message: error instanceof Error ? error.message : String(error),
      recordFailed: false,
    };
  }
}

const DONE_MESSAGES: Record<QueueJob['decision'], string> = {
  approve: 'Cambio aplicado en DV360',
  reject: 'Sugerencia descartada',
  'mark-applied': 'Marcada como realizada',
};

/** One toast for the whole run: the outcome of a single job, or the counts of a batch. */
function summarize(jobs: QueueJob[]): void {
  if (jobs.length === 1) {
    const [job] = jobs;
    if (job.status === 'done' && !job.recordFailed) toast.success(`${DONE_MESSAGES[job.decision]}: ${job.label}`);
    else if (job.recordFailed) toast.error(`Aplicada, pero no se registró en Advia OS: ${job.label}`);
    else if (job.errorCode === STALE_SUGGESTION_CODE) toast.error(`DV360 ha cambiado desde el informe: ${job.label}`);
    else toast.error(`No se pudo completar «${job.label}»: ${job.message}`);
    return;
  }
  const done = jobs.filter((job) => job.status === 'done' && !job.recordFailed).length;
  const stale = jobs.filter((job) => job.errorCode === STALE_SUGGESTION_CODE).length;
  const failed = jobs.length - done - stale;
  const parts = [`${done} hechos`];
  if (stale) parts.push(`${stale} desactualizados`);
  if (failed) parts.push(`${failed} con error`);
  const text = `${jobs.length} cambios: ${parts.join(', ')}`;
  if (failed || stale) toast.error(text);
  else toast.success(text);
}

/**
 * Sends the queued decisions one by one. Mounted once in the Layout, which
 * stays mounted across routes, so the queue keeps running while the trader
 * navigates. After each job the execution's report and history are refetched,
 * so its row updates as soon as it finishes.
 */
export function OptimizationQueueRunner() {
  const jobs = useAtomValue(queueJobsAtom);
  const updateJob = useSetAtom(updateJobAtom);
  const queryClient = useQueryClient();
  const running = useRef(false);
  const finishedInRun = useRef<QueueJob[]>([]);

  useEffect(() => {
    if (running.current) return;
    const next = jobs.find((job) => job.status === 'queued');
    if (!next) {
      if (finishedInRun.current.length > 0) {
        summarize(finishedInRun.current);
        finishedInRun.current = [];
      }
      return;
    }

    running.current = true;
    updateJob({ id: next.id, status: 'running' });
    void runJob(next).then((outcome) => {
      finishedInRun.current.push({ ...next, ...outcome });
      running.current = false;
      updateJob(outcome);
      for (const key of ['optimization-report', 'optimization-reports', 'applied-optimizations']) {
        void queryClient.invalidateQueries({ queryKey: [key, next.executionId] });
      }
    });
  }, [jobs, updateJob, queryClient]);

  const hasActive = jobs.some(isActive);
  useEffect(() => {
    if (!hasActive) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasActive]);

  return null;
}
